import type { EnrollmentFormData } from '@/stores'
import {
  APPLICATION_READY_STORAGE_KEY,
  type EnrollmentDraftStorageKey
} from './enrollmentDraftKeys'
import {
  readDraftBooleanString,
  readDraftNumberString,
  readDraftString
} from './enrollmentDraftReaders'

type EnrollmentDraftData = Partial<
  Pick<
    EnrollmentFormData,
    | 'firstName'
    | 'lastName'
    | 'middleName'
    | 'email'
    | 'phone'
    | 'phoneNumber'
    | 'dateOfBirth'
    | 'birthDate'
    | 'address'
    | 'addressLine1'
    | 'addressLine2'
    | 'city'
    | 'state'
    | 'zipCode'
    | 'about'
    | 'referredBy'
    | 'termsAccepted'
    | 'smsConsent'
    | 'marketingConsent'
    | 'contactExternalId'
    | 'quoteToken'
    | 'locationExternalId'
    | 'locationState'
    | 'programExternalId'
    | 'programYardOnly'
    | 'programClassOnly'
    | 'applicationReadyAt'
  >
>

type StoredEnrollmentFormRecord = Record<string, unknown>
type DraftValueReader = (
  formData: EnrollmentFormData,
  stored: StoredEnrollmentFormRecord
) => string

const draftValueReaders: Partial<Record<EnrollmentDraftStorageKey, DraftValueReader>> = {
  firstName: (formData, stored) => readDraftString(formData.firstName, stored.firstName),
  lastName: (formData, stored) => readDraftString(formData.lastName, stored.lastName),
  middleName: (formData, stored) => readDraftString(formData.middleName, stored.middleName),
  email: (formData, stored) => readDraftString(formData.email, stored.email),
  phone: (formData, stored) => readDraftString(formData.phone, stored.phone),
  phoneNumber: (formData, stored) =>
    readDraftString(formData.phoneNumber || formData.phone, stored.phoneNumber ?? stored.phone),
  birthDate: (formData, stored) =>
    readDraftString(formData.birthDate, stored.birthDate ?? stored.dateOfBirth),
  dateOfBirth: (formData, stored) =>
    readDraftString(formData.dateOfBirth, stored.dateOfBirth ?? stored.birthDate),
  address: (formData, stored) => readDraftString(formData.address, stored.address),
  addressLine1: (formData, stored) => readDraftString(formData.addressLine1, stored.addressLine1),
  addressLine2: (formData, stored) => readDraftString(formData.addressLine2, stored.addressLine2),
  city: (formData, stored) => readDraftString(formData.city, stored.city),
  state: (formData, stored) => readDraftString(formData.state, stored.state),
  zipCode: (formData, stored) => readDraftString(formData.zipCode, stored.zipCode),
  contactExternalId: (formData, stored) =>
    readDraftString(formData.contactExternalId, stored.contactExternalId),
  quoteToken: (formData, stored) => readDraftString(formData.quoteToken, stored.quoteToken),
  'selected-location-external-id': (formData, stored) =>
    readDraftString(formData.locationExternalId, stored.locationExternalId),
  'selected-location-state': (formData, stored) =>
    readDraftString(formData.locationState, stored.locationState),
  'selected-program-external-id': (formData, stored) =>
    readDraftString(formData.programExternalId, stored.programExternalId),
  'selected-program-yard-only': (formData, stored) =>
    readDraftBooleanString(formData.programYardOnly, stored.programYardOnly),
  'selected-program-class-only': (formData, stored) =>
    readDraftBooleanString(formData.programClassOnly, stored.programClassOnly),
  [APPLICATION_READY_STORAGE_KEY]: (formData, stored) =>
    readDraftNumberString(formData.applicationReadyAt, stored.applicationReadyAt)
}

export const buildEnrollmentDraftSnapshot = (
  formData: EnrollmentFormData,
  stored: StoredEnrollmentFormRecord
): EnrollmentDraftData => {
  return {
    firstName: readDraftString(formData.firstName, stored.firstName),
    lastName: readDraftString(formData.lastName, stored.lastName),
    middleName: readDraftString(formData.middleName, stored.middleName),
    email: readDraftString(formData.email, stored.email),
    phone: readDraftString(formData.phone, stored.phone),
    phoneNumber: readDraftString(
      formData.phoneNumber || formData.phone,
      stored.phoneNumber ?? stored.phone
    ),
    dateOfBirth: readDraftString(formData.dateOfBirth, stored.dateOfBirth ?? stored.birthDate),
    birthDate: readDraftString(formData.birthDate, stored.birthDate ?? stored.dateOfBirth),
    address: readDraftString(formData.address, stored.address),
    addressLine1: readDraftString(formData.addressLine1, stored.addressLine1),
    addressLine2: readDraftString(formData.addressLine2, stored.addressLine2),
    city: readDraftString(formData.city, stored.city),
    state: readDraftString(formData.state, stored.state),
    zipCode: readDraftString(formData.zipCode, stored.zipCode),
    about: readDraftString(formData.about, stored.about),
    referredBy: readDraftString(formData.referredBy, stored.referredBy),
    termsAccepted: formData.termsAccepted || stored.termsAccepted === true,
    smsConsent: formData.smsConsent || stored.smsConsent === true,
    marketingConsent: formData.marketingConsent || stored.marketingConsent === true,
    contactExternalId: readDraftString(formData.contactExternalId, stored.contactExternalId),
    quoteToken: readDraftString(formData.quoteToken, stored.quoteToken),
    locationExternalId: readDraftString(formData.locationExternalId, stored.locationExternalId),
    locationState: readDraftString(formData.locationState, stored.locationState),
    programExternalId: readDraftString(formData.programExternalId, stored.programExternalId),
    programYardOnly:
      typeof formData.programYardOnly === 'boolean'
        ? formData.programYardOnly
        : stored.programYardOnly === true,
    programClassOnly:
      typeof formData.programClassOnly === 'boolean'
        ? formData.programClassOnly
        : stored.programClassOnly === true,
    applicationReadyAt:
      formData.applicationReadyAt ??
      (typeof stored.applicationReadyAt === 'number' ? stored.applicationReadyAt : null)
  }
}

export const readEnrollmentDraftFieldValue = (
  formData: EnrollmentFormData,
  stored: StoredEnrollmentFormRecord,
  key: EnrollmentDraftStorageKey
) => {
  const reader = draftValueReaders[key]
  return reader ? reader(formData, stored) : ''
}

export type { EnrollmentDraftData, StoredEnrollmentFormRecord }
